/* eslint-disable react/prop-types */
/**
 * MCC Card Stack
 * ==============
 * Tester-side card stack for MOT Context Capture. Renders inside the MOT
 * Workspace page; the parent loads the outcome + entries and passes them
 * in. LEVEL 4 cards open as a blocking modal automatically (the parent
 * controls the modal flag). LEVEL 2/3 are inline and skippable.
 *
 * Spec rules enforced here:
 *   * OBSERVED / SUSPECTED / CONFIRMED are SEPARATE multi-selects — they
 *     are NEVER merged into a single options list.
 *   * No typing forced. Single optional short note field (500 char cap).
 *   * Severity = 1 tap. Wear pattern = multi-tap. Photos = one-tap camera.
 *   * Card completes in 20–45 s; "Save & Next" advances through the stack.
 *   * "Further investigation required" → escalation button → Secondary
 *     Technical Assessment task. MOT workflow not blocked.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Camera,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  Loader2,
  Stethoscope,
  Pencil,
} from "lucide-react";
import PhotoAnnotator from "@/components/shared/PhotoAnnotator";

const LEVEL_STYLES = {
  4: { ring: "ring-red-500",   pill: "bg-red-600 text-white",        label: "REQUIRED" },
  3: { ring: "ring-amber-500", pill: "bg-amber-500 text-white",      label: "RECOMMENDED" },
  2: { ring: "ring-slate-400", pill: "bg-slate-200 text-slate-700",  label: "OPTIONAL" },
};

function MultiTapPills({ options, value, onChange, testidPrefix }) {
  const safeValue = value || [];
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const on = safeValue.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() =>
              onChange(on ? safeValue.filter((v) => v !== opt) : [...safeValue, opt])
            }
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              on
                ? "bg-teal-500 text-slate-900 border-teal-400"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
            data-testid={`${testidPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function SingleTapPills({ options, value, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const on = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(on ? null : opt)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              on
                ? "bg-indigo-500 text-white border-indigo-400"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
            data-testid={`${testidPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function PhotoStrip({ photos, onAdd, onEdit, busy }) {
  const safePhotos = photos || [];
  const fileRef = useRef(null);
  // iter198nn — Hold the raw camera image while the tester annotates
  // it. `pendingDataUrl == null` means the annotator is closed.
  const [pendingDataUrl, setPendingDataUrl] = useState(null);
  // iter211 — Photo annotation gap fix: tester can re-open the
  // annotator on an existing thumbnail via the ✎ Edit button. We
  // track which thumbnail index is being edited so onSave swaps
  // that slot instead of appending.
  const [editingIndex,    setEditingIndex]    = useState(null);
  const [editingPhotoUrl, setEditingPhotoUrl] = useState(null);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPendingDataUrl(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = ""; // allow re-pick of same file
  };
  const openEditAnnotator = (i, p) => {
    const url = typeof p === "string" ? p : (p?.url || "");
    if (!url) return;
    setEditingIndex(i);
    setEditingPhotoUrl(url);
  };
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1">
      {safePhotos.map((p, i) => {
        const url = typeof p === "string" ? p : (p?.url || "");
        return (
          <div key={i} className="relative group/photo shrink-0" data-testid={`mcc-photo-thumb-wrap-${i}`}>
            <img
              src={url}
              alt={(typeof p === "object" ? p?.label : "") || `evidence-${i}`}
              className="w-16 h-16 rounded-lg object-cover border border-slate-300"
              data-testid={`mcc-photo-thumb-${i}`}
            />
            {onEdit && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openEditAnnotator(i, p); }}
                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-slate-900/80 text-white flex items-center justify-center opacity-0 group-hover/photo:opacity-100 focus:opacity-100 transition-opacity hover:bg-slate-900"
                title="Annotate this photo"
                aria-label={`Annotate photo ${i + 1}`}
                data-testid={`mcc-photo-edit-${i}`}
              >
                <Pencil className="w-3 h-3" />
              </button>
            )}
          </div>
        );
      })}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFile}
        className="hidden"
        data-testid="mcc-photo-input"
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="w-16 h-16 rounded-lg bg-teal-50 border-2 border-dashed border-teal-400 flex flex-col items-center justify-center text-teal-700 hover:bg-teal-100 transition-colors disabled:opacity-50"
        data-testid="mcc-photo-add"
      >
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
        <span className="text-[10px] mt-0.5 font-semibold">PHOTO</span>
      </button>
      {pendingDataUrl && (
        <PhotoAnnotator
          sourceDataUrl={pendingDataUrl}
          onSave={(annotatedUrl) => {
            setPendingDataUrl(null);
            onAdd(annotatedUrl);
          }}
          onCancel={() => setPendingDataUrl(null)}
        />
      )}
      {editingPhotoUrl && (
        <PhotoAnnotator
          photo={editingPhotoUrl}
          onSave={(annotatedUrl, meta) => {
            const idxBeingEdited = editingIndex;
            setEditingPhotoUrl(null);
            setEditingIndex(null);
            // Only persist if the tester actually drew something — a
            // no-op save is treated as Cancel so we don't churn the
            // audit log.
            if (!meta?.annotations_count) return;
            onEdit?.(idxBeingEdited, annotatedUrl);
          }}
          onCancel={() => { setEditingPhotoUrl(null); setEditingIndex(null); }}
        />
      )}
    </div>
  );
}

export default function MCCCardStack({
  outcomeId,
  entries,
  schemas,
  onClose,
  onCompleteAll,
}) {
  // Sort: LEVEL 4 first (most urgent), then 3, then 2. Skipped/complete pushed last.
  const ordered = useMemo(() => {
    const open = entries.filter((e) => e.status === "pending");
    const done = entries.filter((e) => e.status !== "pending");
    open.sort((a, b) => (b.level || 0) - (a.level || 0));
    return [...open, ...done];
  }, [entries]);

  const [idx, setIdx] = useState(0);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [err, setErr] = useState("");

  const current = ordered[idx];

  // Seed the draft whenever the active entry changes.
  useEffect(() => {
    if (!current) return;
    setDraft({
      severity_choice: current.severity_choice || null,
      wear_pattern: current.wear_pattern || [],
      observed: current.observed || [],
      suspected: current.suspected || [],
      confirmed: current.confirmed || [],
      measurements: current.measurements || {},
      recommendation: current.recommendation || null,
      technician_note: current.technician_note || "",
      photos: current.photos || [],
    });
    setErr("");
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!current) {
    return (
      <Card className="p-6 text-center">
        <Check className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
        <p className="font-bold">All context captured.</p>
        <Button className="mt-3" onClick={onClose} data-testid="mcc-close-empty">
          Back to worksheet
        </Button>
      </Card>
    );
  }

  const schema = schemas[current.card_type] || schemas.generic || {};
  const sty = LEVEL_STYLES[current.level] || LEVEL_STYLES[2];
  const showBrakeWear = current.card_type === "brake_disc" && schema.wear_pattern_multi;

  const persist = async (extraPatch = {}, opts = {}) => {
    setSaving(true);
    setErr("");
    try {
      const payload = { ...draft, ...extraPatch, auto_complete: !!opts.autoComplete };
      const r = await axios.put(
        `${API}/mcc/outcomes/${outcomeId}/entries/${current.id}`,
        payload,
      );
      return r.data?.entry;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndNext = async () => {
    const saved = await persist({}, { autoComplete: true });
    if (!saved) return;
    if (idx < ordered.length - 1) {
      setIdx(idx + 1);
    } else {
      onCompleteAll?.();
    }
  };

  const handleSkip = async () => {
    if (current.level >= 4) {
      setErr("LEVEL 4 cards cannot be skipped — capture minimum evidence or escalate.");
      return;
    }
    const saved = await persist({ status: "skipped" });
    if (!saved) return;
    if (idx < ordered.length - 1) setIdx(idx + 1);
    else onCompleteAll?.();
  };

  const handleEscalate = async () => {
    setSaving(true);
    setErr("");
    try {
      await axios.post(
        `${API}/mcc/outcomes/${outcomeId}/entries/${current.id}/escalate`,
        { reason: draft.technician_note || "Further investigation required" },
      );
      if (idx < ordered.length - 1) setIdx(idx + 1);
      else onCompleteAll?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Escalation failed");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoAdd = async (dataUrl) => {
    setPhotoBusy(true);
    try {
      const r = await axios.post(
        `${API}/mcc/outcomes/${outcomeId}/entries/${current.id}/photo`,
        { url: dataUrl, label: "" },
      );
      const newPhotos = r.data?.entry?.photos || [];
      setDraft((d) => ({ ...d, photos: newPhotos }));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Photo upload failed");
    } finally {
      setPhotoBusy(false);
    }
  };

  const handlePhotoEdit = async (photoIndex, dataUrl) => {
    // iter211 — Photo annotation gap fix: PATCH the existing slot
    // instead of appending so we don't pollute the strip with
    // duplicates each time the tester re-annotates.
    setPhotoBusy(true);
    try {
      const r = await axios.patch(
        `${API}/mcc/outcomes/${outcomeId}/entries/${current.id}/photo/${photoIndex}`,
        { url: dataUrl },
      );
      const newPhotos = r.data?.entry?.photos || [];
      setDraft((d) => ({ ...d, photos: newPhotos }));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Annotation save failed");
    } finally {
      setPhotoBusy(false);
    }
  };

  return (
    <Card className={`p-4 ring-2 ${sty.ring}`} data-testid={`mcc-card-${current.id}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${sty.pill}`}>
            {sty.label}
          </span>
          <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">
            {schema.label || "Technical Context"} · {idx + 1} / {ordered.length}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-slate-400 hover:text-slate-700"
          data-testid="mcc-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <p className="text-sm font-semibold text-slate-800 mb-3" data-testid="mcc-defect-text">
        {current.defect_text}
      </p>

      {/* iter198qq — FOH bounce-back reason surfaced inside the
          legacy MCC card too, in case the rapid-capture schema path
          isn't active. Same UX as MOTRapidCaptureCard. */}
      {(current.bounce_back_reason || "").trim() && (
        <div
          className="mb-3 rounded-md border border-purple-300 bg-purple-50 px-3 py-2 text-xs"
          data-testid={`mcc-foh-return-reason-${current.id}`}
        >
          <p className="font-bold text-purple-900 uppercase tracking-wide">
            FOH returned this — please action
          </p>
          <p className="text-purple-900 mt-0.5 italic">"{current.bounce_back_reason}"</p>
        </div>
      )}

      {/* Severity */}
      <div className="mb-3">
        <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1.5">Severity</p>
        <SingleTapPills
          options={schema.severities || ["Light", "Moderate", "Heavy"]}
          value={draft.severity_choice}
          onChange={(v) => setDraft({ ...draft, severity_choice: v })}
          testidPrefix="mcc-sev"
        />
      </div>

      {/* Brake-disc only — wear pattern multi-select */}
      {showBrakeWear && (
        <div className="mb-3">
          <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1.5">Wear pattern</p>
          <MultiTapPills
            options={schema.wear_pattern_multi}
            value={draft.wear_pattern}
            onChange={(v) => setDraft({ ...draft, wear_pattern: v })}
            testidPrefix="mcc-wear"
          />
        </div>
      )}

      {/* OBSERVED / SUSPECTED / CONFIRMED — separate per spec */}
      {schema.observed_options?.length > 0 && (
        <div className="mb-3">
          <p className="text-[11px] font-bold text-emerald-700 uppercase tracking-wide mb-1.5">Observed</p>
          <MultiTapPills
            options={schema.observed_options}
            value={draft.observed}
            onChange={(v) => setDraft({ ...draft, observed: v })}
            testidPrefix="mcc-observed"
          />
        </div>
      )}
      {schema.suspected_options?.length > 0 && (
        <div className="mb-3">
          <p className="text-[11px] font-bold text-amber-700 uppercase tracking-wide mb-1.5">Suspected</p>
          <MultiTapPills
            options={schema.suspected_options}
            value={draft.suspected}
            onChange={(v) => setDraft({ ...draft, suspected: v })}
            testidPrefix="mcc-suspected"
          />
        </div>
      )}
      {schema.confirmed_options?.length > 0 && (
        <div className="mb-3">
          <p className="text-[11px] font-bold text-indigo-700 uppercase tracking-wide mb-1.5">Confirmed</p>
          <MultiTapPills
            options={schema.confirmed_options}
            value={draft.confirmed}
            onChange={(v) => setDraft({ ...draft, confirmed: v })}
            testidPrefix="mcc-confirmed"
          />
        </div>
      )}

      {/* Optional measurements (brake_disc) */}
      {schema.measurements?.length > 0 && (
        <div className="mb-3">
          <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1.5">Measurements (optional)</p>
          <div className="grid grid-cols-3 gap-2">
            {schema.measurements.map((m) => (
              <input
                key={m.key}
                type="number"
                step="0.1"
                placeholder={m.label}
                value={draft.measurements?.[m.key] ?? ""}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    measurements: { ...draft.measurements, [m.key]: e.target.value },
                  })
                }
                className="px-2 py-1.5 text-xs border border-slate-300 rounded bg-white"
                data-testid={`mcc-meas-${m.key}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Recommendation */}
      <div className="mb-3">
        <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1.5">Recommendation</p>
        <SingleTapPills
          options={schema.recommendations || []}
          value={draft.recommendation}
          onChange={(v) => setDraft({ ...draft, recommendation: v })}
          testidPrefix="mcc-rec"
        />
      </div>

      {/* Photos — one-tap mobile camera */}
      <div className="mb-3">
        <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1.5">
          Photos {schema.photo_hints?.length ? `· ${schema.photo_hints.join(" / ")}` : ""}
        </p>
        <PhotoStrip photos={draft.photos} onAdd={handlePhotoAdd} onEdit={handlePhotoEdit} busy={photoBusy} />
      </div>

      {/* Optional short note */}
      <div className="mb-3">
        <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1.5">
          Note (optional, short)
        </p>
        <Textarea
          value={draft.technician_note}
          onChange={(e) =>
            setDraft({ ...draft, technician_note: e.target.value.slice(0, 500) })
          }
          placeholder="e.g. Inner disc faces deteriorated more heavily than outer."
          className="text-xs min-h-[44px]"
          data-testid="mcc-note"
        />
      </div>

      {err && (
        <div className="mb-3 text-xs text-red-600 flex items-center gap-1">
          <AlertTriangle className="w-3.5 h-3.5" /> {err}
        </div>
      )}

      {/* Action bar */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIdx(Math.max(0, idx - 1))}
          disabled={idx === 0 || saving}
          data-testid="mcc-prev"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSkip}
          disabled={saving || current.level >= 4}
          className="text-slate-500"
          data-testid="mcc-skip"
        >
          Skip
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleEscalate}
          disabled={saving}
          className="border-purple-300 text-purple-700 hover:bg-purple-50"
          data-testid="mcc-escalate"
        >
          <Stethoscope className="w-3.5 h-3.5 mr-1" />
          Escalate
        </Button>
        <div className="flex-1" />
        <Button
          size="sm"
          onClick={handleSaveAndNext}
          disabled={saving}
          className="bg-teal-600 hover:bg-teal-700 text-white"
          data-testid="mcc-save-next"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
          {idx < ordered.length - 1 ? "Save & Next" : "Save & Finish"}
          {idx < ordered.length - 1 && <ChevronRight className="w-3.5 h-3.5 ml-1" />}
        </Button>
      </div>
    </Card>
  );
}
